// store/slices/ideas.ts — paginated Story Ideas table view + live-prepend.
//
// Mirrors the users slice (streaming-cursor exception: ONLY the current page
// lives in Redux). Adds a prependFresh action so the reducer-bridge can
// splice a newly-submitted idea onto page 1 when the current view is on the
// default sort/page (created_at desc + page 1). Other views just bump the
// dashboard counter.

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  AdminStoryIdea,
  AdminStoryIdeaStatus,
  AdminStoryIdeasSort,
} from "../types";

export interface IdeasQuery {
  q: string;
  status: "all" | AdminStoryIdeaStatus;
  sort: AdminStoryIdeasSort;
  order: "asc" | "desc";
  page: number;
  pageSize: number;
}

export interface IdeasState {
  query: IdeasQuery;
  currentPage: AdminStoryIdea[];
  total: number;
  page: number;
  pageSize: number;
  /** id of the latest in-flight request — used to drop stale acks. */
  lastRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: IdeasState = {
  query: {
    q: "",
    status: "all",
    sort: "created_at",
    order: "desc",
    page: 1,
    pageSize: 25,
  },
  currentPage: [],
  total: 0,
  page: 1,
  pageSize: 25,
  lastRequestId: null,
  loading: false,
  error: null,
};

const ideas = createSlice({
  name: "ideas",
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<Partial<IdeasQuery>>) {
      state.query = { ...state.query, ...action.payload };
    },
    requestStarted(state, action: PayloadAction<{ requestId: string }>) {
      state.lastRequestId = action.payload.requestId;
      state.loading = true;
      state.error = null;
    },
    requestSucceeded(
      state,
      action: PayloadAction<{
        requestId: string;
        ideas: AdminStoryIdea[];
        total: number;
        page: number;
        pageSize: number;
      }>,
    ) {
      // Drop stale acks — only the most recent request's result wins.
      if (state.lastRequestId !== action.payload.requestId) return;
      state.currentPage = action.payload.ideas;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.loading = false;
      state.error = null;
    },
    requestFailed(
      state,
      action: PayloadAction<{ requestId: string; error: string }>,
    ) {
      if (state.lastRequestId !== action.payload.requestId) return;
      state.loading = false;
      state.error = action.payload.error;
    },
    /**
     * Live-prepend an idea row when a story_idea_submitted event arrives AND
     * the current view is on page 1 + sort=created_at desc + status="all"
     * (i.e. the default). Drops the last row if over pageSize. Bumps total.
     *
     * The event payload only carries bodyPreview (200 chars) — the slice
     * stores it in the body field of the prepended row. Expanding a
     * live-added row therefore shows only the preview; refreshing the
     * page (via re-query) re-hydrates the full body from the DB. This is
     * documented as acceptable for v1.
     */
    prependFresh(state, action: PayloadAction<AdminStoryIdea>) {
      // Defensive: don't double-prepend if a re-query just pulled the same
      // row back in.
      if (state.currentPage.some((i) => i.id === action.payload.id)) return;
      state.currentPage.unshift(action.payload);
      if (state.currentPage.length > state.pageSize) {
        state.currentPage.length = state.pageSize;
      }
      state.total += 1;
    },
    clear() {
      return initialState;
    },
  },
});

export default ideas;
